import CancelIcon from "@/assets/xmark.png";
import Button from "@/components/Common/Buttons/Button";
import Modal from "@/components/Common/Modals";
import { useLoggedIn } from "@/routes/isLoggedIn";
import { RootState } from "@/store";
import { getTenantFromHash, lightenHex } from "@/utils/getBrandings";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Piggy from "../assets/PiggyBank.svg";
import DolphinBoard from "../assets/SootchyDoom.svg";
import Clock from "../assets/clock.png";
import Flower from "../assets/flower.png";
/* import Match from "../assets/match.png"; */
import WelcomeDolphin from "../assets/welcomeDolphin.svg";
import { PrimaryButton } from "../components/Common/Buttons";
import ContentWrapper from "../components/Common/Containers/ContentContainer";
import FrequentlyAskedQuestions from "../components/Common/FAQs";
import StateTaxCalculator from "../components/Common/Select/StateTaxBenefitsComponent";

const SEPIntroduction: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [, setEmployer] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const user = useSelector((state: RootState) => state.user);

  const getCodeParam = () => {
    const standardCode = searchParams.get("code");
    if (standardCode) return standardCode;

    // Fallback: parse from hash for Safari/Firefox compatibility
    try {
      const hash = window.location.hash;
      const hashParams = new URLSearchParams(hash.split("?")[1] || "");
      return hashParams.get("code");
    } catch {
      return null;
    }
  };

  const code = getCodeParam();

  const location = useLocation();

  const isAuthenticated = useLoggedIn();

  const isSameUser = (visitorUsername: string) => {
    return user.username === visitorUsername;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  const logoutUser = () => {
    localStorage.clear();
    window.location.href = `/sep-introduction?code=${code || ""}`;
    window.location.reload();
    return false;
  };

  useEffect(() => {
    if (code?.length) {
      try {
        const decodedData = atob(code);

        const jsonData = JSON.parse(decodedData);

        if (isAuthenticated && !isSameUser(jsonData.username)) {
          //logoutUser();
          setShowLogoutModal(true);
          //return;
        }

        if (jsonData.firstname) setFirstName(jsonData.firstname);
        if (jsonData.lastname) setLastName(jsonData.lastname);
        if (jsonData.companyName) setEmployer(jsonData.companyName);
      } catch {
        setHasError(true);
      }
    }
  }, [code, isAuthenticated]);

  if (isLoading) {
    return (
      <ContentWrapper>
        <div className="font-outfit flex justify-center items-center min-h-screen">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-500 rounded-full animate-spin"></div>
            <div className="mt-4 text-teal-600 text-[1rem] font-medium">
              {t("introduction_page.loading")}
            </div>
          </div>
        </div>
      </ContentWrapper>
    );
  }

  if (hasError) {
    return (
      <ContentWrapper>
        <div className="font-outfit flex flex-col justify-center items-center min-h-screen">
          <div className="text-lg text-red-600 mb-4">
            {t("introduction_page.error_message")}
          </div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-blue-600"
          >
            {t("introduction_page.try_again")}
          </button>
        </div>
      </ContentWrapper>
    );
  }

  const partner = getTenantFromHash();
  const gradientFrom = partner ? window.partner.colors.primary : "#58D4D4";
  const gradientTo = partner
    ? lightenHex(window.partner.colors.primary, 0.85)
    : "#ADF7F7";

  return (
    <>
      <ContentWrapper>
        <div className="container mx-auto px-0 lg:px-4 lg:my-[30px] font-outfit">
          {/** Hero Section */}
          <div
            style={{
              background: `linear-gradient(to bottom right, ${gradientFrom}, ${gradientTo})`,
            }}
            className="hidden lg:block relative h-[490px] rounded-[24px]"
          >
            <div
              id="hero"
              className="h-full z-30 flex flex-col lg:flex-row justify-center items items-center pt-[20px]"
            >
              <div className="w-[90%] lg:w-[550px]">
                <h2 className="text-center md:text-left text-[20px] md:text-[29px] pb-[8px] lg:pb-[22px] leading-[127%] font-semibold text-white">
                  {t("introduction_page.hero.sootchy_education_program")}
                </h2>

                <h1 className="text-center md:text-left text-wrap text-[24px] md:text-[40px] leading-[127%] font-semibold text-black">
                  {t("introduction_page.hero.header")}
                </h1>

                <p className="text-[18px] leading-[127%] font-normal pt-[16px] ">
                  {t("introduction_page.hero.description")}
                </p>
              </div>

              <img
                id="dolphin"
                className="w-[220px] md:w-[400px]  mt-[20px] lg:mt-0"
                src={WelcomeDolphin}
                alt="Welcome Dolphin"
              />

              <img
                id="board"
                className="absolute bottom-0 z-10 w-[80%]"
                src={DolphinBoard}
                alt="Sootchy Board"
              />
            </div>
          </div>

          <div
            style={{
              background: `linear-gradient(to bottom right, ${gradientFrom}, ${gradientTo})`,
            }}
            // className="lg:hidden block relative h-[390px]  bg-gradient-to-b from-[#58D4D4] to-[#ADF7F7]"
            className="lg:hidden block relative h-[390px]  bg-gradient-to-b "
          >
            <div
              id="hero_mobile"
              className="relative  h-full flex flex-col  items items-end pt-[40px]"
            >
              <div className="w-full px-4">
                <h2 className="text-[22px] md:text-[29px] pb-[8px] lg:pb-[22px] leading-[127%] font-semibold text-white">
                  {t("introduction_page.hero.sootchy_education_program")}
                </h2>

                <h1 className="text-left text-wrap text-[28px] md:text-[38px] leading-[127%] font-semibold text-black">
                  {t("introduction_page.hero.header")}
                </h1>

                <p className="text-[18px] leading-[127%] font-normal pt-[16px] ">
                  {t("introduction_page.hero.description")}
                </p>
              </div>

              <img
                id="dolphin"
                className="absolute z-10 bottom-[10px] w-[200px] md:w-[40%]  mt-[20px] lg:mt-0"
                src={WelcomeDolphin}
                alt="Welcome Dolphin"
              />

              <img
                id="board"
                className="absolute bottom-0 z-5"
                src={DolphinBoard}
                alt="Sootchy Board"
              />
            </div>
          </div>

          {code && (
            <>
              <div
                id="invite_mobile"
                className="flex flex-col justify-between lg:hidden  relative  mx-auto  shadow-md px-4 py-[32px]"
              >
                <div>
                  <h3 className="text-[24px] font-semibold leading-[127%]">
                    {t("introduction_page.invitation.congratulations", {
                      firstName,
                      lastName,
                    })}
                  </h3>
                  <p className="text-[18px] leading-[127%] font-normal pt-[16px] ">
                    {t("introduction_page.invitation.invitation_message")}
                  </p>
                </div>

                {/* <button
                  data-testid="get-started-button"
                  className="btn px-[56px] py-[12px] mt-[32px] rounded-[12px]"
                  onClick={() => navigate("/login")}
                >
                  Get started
                </button> */}
                <div className="mt-[32px]">
                  <PrimaryButton
                    isFullWidth
                    //handleClick={() => navigate(`/login?employee=${code}`)}
                    handleClick={() =>
                      navigate(`/create-password?employee=${code}`)
                    }
                    isLoading={false}
                    buttonType="default"
                    type="submit"
                    label={t("introduction_page.invitation.get_started")}
                    dataTestID="login-redirect-btn"
                  />
                </div>
              </div>
              <div
                id="invite"
                className="hidden lg:flex flex-col justify-between relative z-40 lg:h-[236px] w-[90%] lg:w-[800px] mx-auto -translate-y-12 shadow-md bg-white rounded-[24px] p-[32px]"
              >
                <div>
                  <h3 className="text-[22px] md:text-[24px] font-semibold leading-[127%]">
                    {t("introduction_page.invitation.congratulations", {
                      firstName,
                      lastName,
                    })}
                  </h3>
                  <p className="text-[16px] leading-[127%] font-normal pt-[16px] ">
                    {t("introduction_page.invitation.invitation_message")}
                  </p>
                </div>
                <div className="md:w-[207px]">
                  {/* <button
                    data-testid="get-started-button"
                    className="btn px-[56px] py-[12px]  rounded-[12px]"
                    onClick={() => navigate("/login")}
                  >
                    Get started
                  </button> */}

                  <PrimaryButton
                    isFullWidth
                    //handleClick={() => navigate("/login")}
                    //handleClick={() => navigate(`/login?employee=${code}`)}
                    handleClick={() =>
                      navigate(`/create-password?employee=${code}`)
                    }
                    isLoading={false}
                    buttonType="default"
                    type="submit"
                    label={t("introduction_page.invitation.get_started")}
                    dataTestID="login-redirect-btn"
                  />
                </div>
              </div>
            </>
          )}

          {!code && (
            <>
              <div
                id="invite_mobile"
                className="flex flex-col justify-between lg:hidden  relative  mx-auto  shadow-md px-4 py-[32px]"
              >
                <div>
                  <h3 className="text-[24px] font-semibold leading-[127%]">
                    {t("introduction_page.invitation.login_to_continue", {
                      firstName,
                      lastName,
                    })}
                  </h3>
                  <p className="text-[18px] leading-[127%] font-normal pt-[16px] ">
                    {t("introduction_page.invitation.invitation_past_message")}
                  </p>
                </div>

                <div className="mt-[32px]">
                  <PrimaryButton
                    isFullWidth
                    //handleClick={() => navigate(`/login?employee=${code}`)}
                    handleClick={() => navigate(`/login`)}
                    isLoading={false}
                    buttonType="default"
                    type="submit"
                    label={t(
                      "introduction_page.invitation.continue_with_login",
                    )}
                    dataTestID="login-redirect-btn"
                  />
                </div>
              </div>
              <div
                id="invite"
                className="hidden lg:flex flex-col justify-between relative z-40 lg:h-[236px] w-[90%] lg:w-[800px] mx-auto -translate-y-12 shadow-md bg-white rounded-[24px] p-[32px]"
              >
                <div>
                  <h3 className="text-[22px] md:text-[24px] font-semibold leading-[127%]">
                    {t("introduction_page.invitation.login_to_continue", {
                      firstName,
                      lastName,
                    })}
                  </h3>
                  <p className="text-[16px] leading-[127%] font-normal pt-[16px] ">
                    {t("introduction_page.invitation.invitation_past_message")}
                  </p>
                </div>
                <div className="md:w-[207px]">
                  {/* <button
                    data-testid="get-started-button"
                    className="btn px-[56px] py-[12px]  rounded-[12px]"
                    onClick={() => navigate("/login")}
                  >
                    Get started
                  </button> */}

                  <PrimaryButton
                    isFullWidth
                    //handleClick={() => navigate("/login")}
                    //handleClick={() => navigate(`/login?employee=${code}`)}
                    handleClick={() => navigate(`/login`)}
                    isLoading={false}
                    buttonType="default"
                    type="submit"
                    label={t(
                      "introduction_page.invitation.continue_with_login",
                    )}
                    dataTestID="login-redirect-btn"
                  />
                </div>
              </div>
            </>
          )}

          {/** Benefits */}
          <div
            //style={{ paddingTop: code ? "0px" : "0px" }}
            className="w-full lg:w-[896px] mx-auto px-4 lg:px-0 pt-[82px]"
          >
            <h2 className="text-left text-wrap text-[24px] md:text-[32px] leading-[127%] font-semibold text-black">
              {t("introduction_page.what_is_sep.title")}
            </h2>

            <p className="pt-[32px] pb-[76px] ">
              {t("introduction_page.what_is_sep.description")}
            </p>
            <h3 className="text-left text-wrap text-[24px] md:text-[32px] leading-[127%] font-semibold text-black">
              {t("introduction_page.benefits.title")}
            </h3>

            <div className="pt-[40px] pb-[96px] grid grid-cols-1 md:grid-cols-2 gap-[24px]">
              <div className="flex w-full h-full">
                <div className="h-[110px] flex justify-center items-center w-[140px] rounded-[16px] bg-brand-light">
                  <img
                    src={Piggy}
                    className="h-[35px] w-[35px] bg-cover"
                    alt="Benefit Icon"
                  />
                </div>
                <div className="w-full h-full">
                  <div className="flex flex-col pl-[20px] space-y-[4px]">
                    <h6 className="text-[18px] font-semibold">
                      {t("introduction_page.benefits.tax_benefits.title")}
                    </h6>
                    <p className="text-[16px] text-wrap">
                      {t("introduction_page.benefits.tax_benefits.description")}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex w-full h-full">
                <div className="h-[110px] flex justify-center items-center w-[140px] rounded-[16px] bg-brand-light">
                  <img
                    src={Flower}
                    className="h-[35px] w-[35px] bg-cover"
                    alt="Benefit Icon"
                  />
                </div>
                <div className="w-full h-full">
                  <div className="flex flex-col px-[20px] space-y-[4px]">
                    <h6 className="text-[18px] font-semibold">
                      {t("introduction_page.benefits.tax_free_growth.title")}
                    </h6>
                    <p className="text-[16px] text-wrap">
                      {t(
                        "introduction_page.benefits.tax_free_growth.description",
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex w-full h-full">
                <div className="h-[110px] flex justify-center items-center w-[140px] rounded-[16px] bg-brand-light">
                  <img
                    src={Clock}
                    className="h-[35px] w-[35px] bg-cover"
                    alt="Benefit Icon"
                  />
                </div>
                <div className="w-full h-full">
                  <div className="flex flex-col px-[20px] space-y-[4px]">
                    <h6 className="text-[18px] font-semibold">
                      {t("introduction_page.benefits.auto_payroll.title")}
                    </h6>
                    <p className="text-[16px] text-wrap">
                      {t("introduction_page.benefits.auto_payroll.description")}
                    </p>
                  </div>
                </div>
              </div>

              {/**Disable briefly */}

              {/* <div className="flex w-full h-full">
                <div className="h-[110px] flex justify-center items-center w-[140px] rounded-[16px] bg-[#E1FBFB]">
                  <img
                    src={Match}
                    className="h-[35px] w-[35px] bg-cover"
                    alt="Benefit Icon"
                  />
                </div>
                <div className="w-full h-full">
                  <div className="flex flex-col px-[20px] space-y-[4px]">
                    <h6 className="text-[18px] font-semibold">
                      {t("introduction_page.benefits.employer_matching.title")}
                    </h6>
                    <p className="text-[16px] text-wrap">
                      {t(
                        "introduction_page.benefits.employer_matching.description"
                      )}
                    </p>
                  </div>
                </div>
              </div> */}
            </div>
          </div>

          {/** Tax benefits */}
          <div
            style={{
              background: `linear-gradient(to bottom right, ${gradientFrom}, ${gradientTo})`,
            }}
            //  className="relative px-4 lg:rounded-[24px] bg-gradient-to-b from-[#58D4D4] to-[#ADF7F7]"
            className="relative px-4 lg:rounded-[24px]"
          >
            <div className="w-full lg:w-[896px] mx-auto  py-[96px]">
              <h2 className="text-center md:text-left text-wrap text-[24px] md:text-[32px] leading-[127%] font-semibold text-black">
                {t("introduction_page.tax_section.title")}
              </h2>
              <p className="text-center md:text-left pt-[32px] pb-[76px] ">
                {t("introduction_page.tax_section.description")}
              </p>

              <div className="w-full rounded-[10px] bg-white grid grid-cols-1 md:grid-cols-2">
                <div className="p-[24px]">
                  <h6 className="text-[18px] font-semibold">
                    {t("introduction_page.tax_section.federal_benefits.title")}
                  </h6>
                  <div className="mt-[12px] mb-[24px] pt-[4px] pb-[5px] px-[8px] bg-[#99EBE1] shadow-custom inline-block rounded-[8px] border-[1.3px] border-black">
                    {t(
                      "introduction_page.tax_section.federal_benefits.save_thousands",
                    )}
                  </div>
                  <p className="mb-[24px]">
                    <span className="font-semibold">
                      {t(
                        "introduction_page.tax_section.federal_benefits.tax_deferral",
                      )}
                    </span>{" "}
                    {t(
                      "introduction_page.tax_section.federal_benefits.tax_deferral_description",
                    )}
                  </p>

                  <p className="mb-[24px]">
                    <span className="font-semibold">
                      {t(
                        "introduction_page.tax_section.federal_benefits.exclusion_from_taxes",
                      )}
                    </span>{" "}
                    {t(
                      "introduction_page.tax_section.federal_benefits.exclusion_description",
                    )}
                  </p>
                  <p className="text-[#7D8688] text-[14px]">
                    {t(
                      "introduction_page.tax_section.federal_benefits.tax_treatment",
                    )}
                  </p>
                </div>
                <div className="p-[24px] md:border-l-[1.3px] ">
                  <h6 className="text-[18px] font-semibold mb-[12px]">
                    {t("introduction_page.tax_section.state_benefits.title")}
                  </h6>

                  <p className="mb-[24px]">
                    {t(
                      "introduction_page.tax_section.state_benefits.description",
                    )}
                  </p>

                  <StateTaxCalculator />
                </div>
              </div>
            </div>
          </div>

          <div
            id="frequently-asked-questions"
            className="w-full lg:w-[896px] mx-auto px-4 lg:px-0 py-[56px]"
          >
            <h2 className="text-left text-wrap text-[24px] md:text-[32px] leading-[127%] font-semibold text-black">
              {t("introduction_page.faq.title")}
            </h2>
            <FrequentlyAskedQuestions />
            <div className="mt-[40px] lg:max-w-[30%]">
              <PrimaryButton
                isFullWidth
                //handleClick={() => navigate("/login")}
                handleClick={
                  code
                    ? () => navigate(`/create-password?employee=${code}`)
                    : () => navigate(`/login`)
                }
                isLoading={false}
                buttonType="default"
                type="submit"
                label={
                  code
                    ? t("introduction_page.invitation.get_started")
                    : t("introduction_page.invitation.continue_with_login")
                }
                dataTestID="login-redirect-btn"
              />
            </div>
          </div>
        </div>
      </ContentWrapper>

      <Modal
        widthSize="md:w-[520px]"
        isOpen={showLogoutModal}
        onCancel={() => setShowLogoutModal(false)}
      >
        <>
          <div className="flex justify-between py-[16px] px-[24px] bg-white">
            <h3 className="text-[18px] font-semibold font-outfit">
              {t("introduction_page.logout_modal.title")}
            </h3>
            <img
              src={CancelIcon}
              onClick={() => setShowLogoutModal(false)}
              className="cursor-pointer w-[18px] h-[18px]"
              alt="Close"
            />
          </div>
          <div className="border-y-[1px] border-[#E2E2E2] bg-[#F9F9F9] p-[24px]">
            {t("introduction_page.logout_modal.message")}
          </div>
          <div className="flex  px-[24px] bg-white ">
            {/*  <Button
              size="small"
              ariaLabel="previous"
              variant="secondary"
              label="Cancel"
              iconVisibility="hide"
              handleClick={() => {
                setShowLogoutModal(false);
                window.location.href = `#/sep-introduction`;
              }}
            /> */}
            <Button
              size="small"
              ariaLabel="previous"
              variant="primary"
              label={t("introduction_page.logout_modal.log_out")}
              iconVisibility="hide"
              handleClick={() => logoutUser()}
            />
          </div>
        </>
      </Modal>
    </>
  );
};

export default SEPIntroduction;
